import React, { useState, useEffect, useRef } from 'react';
import { AlertCircle, X, ExternalLink, Clock, Phone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function NotificationAlertModal() {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [currentAlert, setCurrentAlert] = useState(null);
  const audioRef = useRef(null);
  const checkIntervalRef = useRef(null);
  const snoozedNotificationsRef = useRef(new Set()); // Track snoozed notifications

  useEffect(() => {
    // Check for due notifications every 10 seconds
    checkIntervalRef.current = setInterval(checkForDueNotifications, 10000);
    
    // Initial check
    checkForDueNotifications();
    
    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, []);

  const checkForDueNotifications = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/scheduled-notifications/check-due`,
        { headers: getAuthHeader() }
      );
      
      if (response.data.due_notifications && response.data.due_notifications.length > 0) {
        // Filter out recently snoozed notifications
        const unSnoozedNotifications = response.data.due_notifications.filter(
          n => !snoozedNotificationsRef.current.has(n.id)
        );
        
        if (unSnoozedNotifications.length > 0) {
          // Show the first due notification that hasn't been snoozed
          const notification = unSnoozedNotifications[0];
          setCurrentAlert(notification);
          
          // Play notification sound
          playNotificationSound();
        }
      }
    } catch (error) {
      console.error('Error checking for due notifications:', error);
    }
  };

  const playNotificationSound = () => {
    // Create a simple beep sound using Web Audio API
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800; // Frequency in Hz
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (error) {
      console.error('Error playing notification sound:', error);
    }
  };

  const handleTakeAction = async () => {
    if (!currentAlert) return;
    
    // Navigate to lead management and open the lead update modal
    // NOTE: Notification will be marked as completed only after user submits an update
    navigate(`/admin/leads?lead_id=${currentAlert.lead_id}&openUpdate=true&notification_id=${currentAlert.id}`);
    
    // Close the alert
    setCurrentAlert(null);
  };

  const handleSnooze = async () => {
    if (!currentAlert) return;
    
    try {
      await axios.put(
        `${BACKEND_URL}/api/scheduled-notifications/${currentAlert.id}/snooze`,
        {},
        { headers: getAuthHeader() }
      );
      
      // Add to snoozed set to prevent immediate re-trigger
      snoozedNotificationsRef.current.add(currentAlert.id);
      
      // Remove from snoozed set after 16 minutes (15 min snooze + 1 min buffer)
      setTimeout(() => {
        snoozedNotificationsRef.current.delete(currentAlert.id);
      }, 16 * 60 * 1000);
      
      // Close the alert
      setCurrentAlert(null);
      
      console.log('✅ Notification snoozed for 15 minutes');
    } catch (error) {
      console.error('Error snoozing notification:', error);
      alert('Failed to snooze notification. Please try again.');
    }
  };

  const formatTime = (dateStr) => {
    // Parse the UTC time from backend
    const utcDate = new Date(dateStr);
    
    // Convert to IST (add 5.5 hours)
    const istOffset = 5.5 * 60 * 60 * 1000; // 5.5 hours in milliseconds
    const istDate = new Date(utcDate.getTime() + istOffset);
    
    // Format in IST - USE getHours() not getUTCHours()!
    const hours = istDate.getHours();
    const minutes = String(istDate.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    
    return `${displayHours}:${minutes} ${ampm}`;
  };

  if (!currentAlert) return null;

  return (
    <>
      {/* Backdrop */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.6)',
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        animation: 'fadeIn 0.2s ease-out'
      }}>
        {/* Alert Modal */}
        <div style={{
          width: '480px',
          maxWidth: '90vw',
          background: 'white',
          borderRadius: '16px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          overflow: 'hidden',
          animation: 'slideIn 0.3s ease-out'
        }}>
          {/* Header with animated pulse */}
          <div style={{
            padding: '24px',
            background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
            color: 'white',
            position: 'relative'
          }}>
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '100%',
              height: '100%',
              background: 'radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%)',
              animation: 'pulse 2s infinite'
            }} />
            
            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '8px'
              }}>
                <AlertCircle size={32} />
                <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '700' }}>
                  Follow-Up Reminder
                </h2>
              </div>
              <p style={{ margin: 0, fontSize: '14px', opacity: 0.95 }}>
                It's time for your scheduled follow-up
              </p>
            </div>
          </div>

          {/* Body */}
          <div style={{ padding: '24px' }}>
            {/* Lead Info */}
            <div style={{
              marginBottom: '20px',
              padding: '16px',
              background: '#f3f4f6',
              borderRadius: '8px',
              border: '2px solid #e5e7eb'
            }}>
              <div style={{
                fontSize: '18px',
                fontWeight: '700',
                color: '#1f2937',
                marginBottom: '8px'
              }}>
                {currentAlert.lead_name}
              </div>
              
              {currentAlert.lead_phone && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px',
                  color: '#6b7280',
                  marginBottom: '4px'
                }}>
                  <Phone size={14} />
                  <span>{currentAlert.lead_phone}</span>
                </div>
              )}
              
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                color: '#374151',
                marginTop: '8px'
              }}>
                <Clock size={14} style={{ color: '#f59e0b' }} />
                <span style={{ fontWeight: '600' }}>
                  Scheduled for {formatTime(currentAlert.scheduled_time)}
                </span>
              </div>
            </div>

            {/* Remarks */}
            <div style={{ marginBottom: '24px' }}>
              <div style={{
                fontSize: '12px',
                fontWeight: '600',
                color: '#6b7280',
                marginBottom: '6px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Remarks:
              </div>
              <div style={{
                padding: '12px',
                background: '#fef3c7',
                border: '2px solid #fbbf24',
                borderRadius: '8px',
                fontSize: '14px',
                color: '#78350f',
                fontStyle: 'italic',
                lineHeight: '1.5'
              }}>
                "{currentAlert.remarks}"
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              gap: '12px'
            }}>
              <button
                onClick={handleTakeAction}
                style={{
                  padding: '14px 20px',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '15px',
                  fontWeight: '700',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'transform 0.2s, box-shadow 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                <ExternalLink size={18} />
                Take Action
              </button>
              
              <button
                onClick={handleSnooze}
                style={{
                  padding: '14px 20px',
                  background: '#a855f7',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '15px',
                  fontWeight: '700',
                  whiteSpace: 'nowrap',
                  transition: 'opacity 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.opacity = '0.9'}
                onMouseLeave={(e) => e.target.style.opacity = '1'}
              >
                Snooze 15m
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes slideIn {
          from {
            transform: translateY(-50px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
      `}</style>
    </>
  );
}
